/**
 * Turn any API error into something safe to put on screen.
 *
 * Every DRF error passes through `modules.common.exceptions
 * .custom_exception_handler`, which re-wraps the body as
 * `{ error: true, detail: <original>, status: <code> }`. So the original DRF
 * payload ends up *nested*:
 *
 *   403 permission  -> { error: true, detail: { detail: "You do not have…" }, status: 403 }
 *   validation      -> { error: true, detail: { member_ids: ["Invalid pk"] }, status: 400 }
 *   handler 500     -> { error: true, detail: "An unexpected error occurred.", status: 500 }
 *
 * Reading `data.detail` therefore yields an *object* for the first two, and
 * passing an object to React throws "Objects are not valid as a React child" —
 * which trips the router error boundary and replaces a perfectly ordinary
 * permission message with a full-page "Something went wrong". That is the bug
 * this module exists to prevent.
 *
 * The recursion here is lifted from the working implementation in
 * pages/BackupRestore.jsx, which hit this first; this is the shared version so
 * there is one behaviour instead of a different guess per page.
 */

/** Reduce any nested DRF payload to a single string ('' when there is none). */
export const messageFrom = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return messageFrom(value[0]);

  if (typeof value === 'object') {
    // The wrapper's own `detail` holds the real payload — unwrap it first.
    if (value.detail !== undefined) return messageFrom(value.detail);

    // Otherwise it is a field-error map: surface the first field.
    // `error`, `status` and `code` are envelope noise, not messages.
    const entries = Object.entries(value).filter(
      ([key]) => !['error', 'status', 'code'].includes(key),
    );
    if (!entries.length) return '';
    const [field, fieldValue] = entries[0];
    const message = messageFrom(fieldValue);
    return message ? `${field}: ${message}` : '';
  }

  return String(value);
};

/**
 * i18n key for a status code, used only when the server sent no usable message.
 * Returns a key rather than a translated string so this module stays free of
 * any i18n import and can be unit-tested on its own.
 */
export const statusMessageKey = (status) => {
  switch (status) {
    case 400: return 'common.error';
    case 401: return 'common.session_expired';
    case 403: return 'common.permission_denied';
    case 404: return 'common.not_found';
    case 429: return 'common.too_many_requests';
    default:
      if (!status) return 'common.network_error';
      return status >= 500 ? 'common.server_error' : 'common.error';
  }
};

/** True when the request never reached the server (offline, DNS, CORS, abort). */
export const isNetworkError = (err) => !err?.response;

/**
 * The string to show the user.
 *
 * Order matters: a message the server bothered to send is more useful than any
 * generic mapping ("This team has targets set against it…" beats "An error
 * occurred"), so the body wins when it yields one.
 *
 * @param {unknown}  err       the rejected axios error
 * @param {Function} t         translate function from useTranslation()
 * @param {string}   [fallback] page-specific wording, preferred over the
 *                              status default when the body says nothing
 * @returns {string} always a string — never an object
 */
export const describeApiError = (err, t, fallback) => {
  const fromBody = messageFrom(err?.response?.data);
  if (fromBody) return fromBody;

  const translate = typeof t === 'function' ? t : (key) => key;
  if (isNetworkError(err)) return translate('common.network_error');

  return fallback || translate(statusMessageKey(err?.response?.status));
};

/** Convenience for the common "did this fail because of permissions?" check. */
export const isPermissionError = (err) => err?.response?.status === 403;
